import {
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  memo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import classNames from "classnames";
import {
  ARROW_DOWN_KEY,
  ARROW_LEFT_KEY,
  ARROW_RIGHT_KEY,
  ARROW_UP_KEY,
  END_KEY,
  ENTER_KEY,
  HOME_KEY,
  PAGE_DOWN_KEY,
  PAGE_UP_KEY,
  SPACE_KEY,
} from "../../../../core/constants/keyboard_keys";
import { SCROLL_SETTLE_DELAY } from "./constants";
import { clamp, getNearestOptionIndex, pad2 } from "./timeValueHelper";

import styles from "./CustomTimePicker.module.css";

/** Lets the popup move keyboard focus between columns. */
export interface TimeColumnHandle {
  focusActiveOption: () => void;
}

export interface TimeColumnProps {
  label: string;
  /**
   * Position of this column. Handed back to `onSelect`/`onFocusColumn` so the
   * popup can pass those callbacks unchanged and this column stays memoized.
   */
  segmentIndex: number;
  options: number[];
  selected?: number;
  itemHeight: number;
  /** Wheel behaviour: the option in the middle of the column is the selected one. */
  scrollMode: boolean;
  /** How far PageUp/PageDown jump, i.e. one screen of options. */
  pageSize: number;
  disabled?: boolean;
  dataCy?: string;
  onSelect: (segmentIndex: number, value: number) => void;
  /** Left/right arrow keys: asks the popup to focus the column at this index. */
  onFocusColumn?: (segmentIndex: number) => void;
  /** Enter or Space on an option: the value is picked, the popup can close. */
  onConfirm?: () => void;
}

/**
 * One scrollable column of time options.
 *
 * In scroll mode the column behaves like a wheel - the centered option is the
 * selected one - while options stay clickable and keyboard operable in both
 * modes. Selection follows focus, so the focused option is always the selected
 * one and a single option per column is in the tab sequence (roving tabindex).
 */
const TimeColumnBase = forwardRef<TimeColumnHandle, TimeColumnProps>(
  (
    {
      label,
      segmentIndex,
      options,
      selected,
      itemHeight,
      scrollMode,
      pageSize,
      disabled,
      dataCy,
      onSelect,
      onFocusColumn,
      onConfirm,
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    /** Where we scrolled the column ourselves, `null` while the user owns it. */
    const programmaticTop = useRef<number | null>(null);
    const settleTimer = useRef<number>();

    const activeIndex = getNearestOptionIndex(options, selected);
    const scrollIndex = Math.max(activeIndex, 0);

    // read by the settle timeout, which must not compare against a stale selection
    const selectedRef = useRef(selected);
    useEffect(() => {
      selectedRef.current = selected;
    }, [selected]);

    const focusOption = (index: number) => {
      const option = listRef.current?.children[index] as
        | HTMLElement
        | undefined;
      option?.focus();
    };

    useImperativeHandle(
      ref,
      () => ({ focusActiveOption: () => focusOption(scrollIndex) }),
      [scrollIndex],
    );

    // keep the selected option centered (also on open, since this runs on mount)
    useLayoutEffect(() => {
      const scroller = scrollRef.current;
      if (!scroller) return;

      const nextScrollTop = scrollIndex * itemHeight;
      if (Math.abs(scroller.scrollTop - nextScrollTop) < 1) return;

      // our own scroll must not be committed back as if the user had picked it
      window.clearTimeout(settleTimer.current);
      programmaticTop.current = nextScrollTop;
      scroller.scrollTop = nextScrollTop;
    }, [scrollIndex, itemHeight]);

    useEffect(
      () => () => {
        window.clearTimeout(settleTimer.current);
      },
      [],
    );

    /** Commits the option the column snapped to once scrolling has stopped. */
    const handleScroll = () => {
      if (!scrollMode || disabled) return;

      const scroller = scrollRef.current;
      if (!scroller) return;

      /*
       * Ignore the scroll we caused ourselves, but hand control back as soon as
       * the column leaves the position we put it at - a timed lock would swallow
       * the beginning of the next real gesture.
       */
      if (programmaticTop.current !== null) {
        if (Math.abs(scroller.scrollTop - programmaticTop.current) < 1) return;
        programmaticTop.current = null;
      }

      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        const settled = scrollRef.current;
        if (!settled) return;

        const index = clamp(
          Math.round(settled.scrollTop / itemHeight),
          0,
          options.length - 1,
        );
        const nextValue = options[index];
        if (nextValue !== selectedRef.current)
          onSelect(segmentIndex, nextValue);
      }, SCROLL_SETTLE_DELAY);
    };

    /** Selection follows focus, so moving also selects. */
    const moveTo = (index: number) => {
      const nextIndex = clamp(index, 0, options.length - 1);
      onSelect(segmentIndex, options[nextIndex]);
      focusOption(nextIndex);
    };

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      switch (e.key) {
        case ARROW_UP_KEY:
          moveTo(scrollIndex - 1);
          break;
        case ARROW_DOWN_KEY:
          moveTo(scrollIndex + 1);
          break;
        case PAGE_UP_KEY:
          moveTo(scrollIndex - pageSize);
          break;
        case PAGE_DOWN_KEY:
          moveTo(scrollIndex + pageSize);
          break;
        case HOME_KEY:
          moveTo(0);
          break;
        case END_KEY:
          moveTo(options.length - 1);
          break;
        case ARROW_LEFT_KEY:
          onFocusColumn?.(segmentIndex - 1);
          break;
        case ARROW_RIGHT_KEY:
          onFocusColumn?.(segmentIndex + 1);
          break;
        case ENTER_KEY:
        case SPACE_KEY:
          // the focused option is already selected, so this only confirms
          onConfirm?.();
          break;
        default:
          // Escape and Tab must reach the popup, which owns them
          return;
      }

      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <div className={styles.column}>
        <span className={styles.columnLabel} aria-hidden="true">
          {label}
        </span>

        <div className={styles.columnListWrapper}>
          {scrollMode && (
            <span className={styles.centerBand} aria-hidden="true" />
          )}

          <div
            ref={scrollRef}
            role="listbox"
            aria-label={label}
            data-cy={dataCy}
            className={classNames(styles.columnList, {
              [styles.columnListSnap]: scrollMode,
            })}
            onScroll={handleScroll}
          >
            {/* role="none" so the listbox keeps owning the options */}
            <div ref={listRef} role="none" className={styles.columnListInner}>
              {options.map((option, index) => {
                const isSelected = index === activeIndex;
                const isTabStop = !disabled && index === scrollIndex;
                return (
                  /*
                   * A div rather than a button on purpose: the shared focus trap
                   * treats every enabled <button> as focusable, which would make
                   * all 60 options boundary candidates and let Shift+Tab escape
                   * the popup. With tabindex the trap sees exactly the one
                   * option per column that is really in the tab sequence.
                   */
                  <div
                    key={option}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={disabled || undefined}
                    // roving tabindex, a column is a single tab stop
                    tabIndex={isTabStop ? 0 : -1}
                    className={classNames(styles.option, {
                      [styles.optionSelected]: isSelected,
                    })}
                    onClick={() => !disabled && onSelect(segmentIndex, option)}
                    onKeyDown={handleKeyDown}
                  >
                    {pad2(option)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

TimeColumnBase.displayName = "TimeColumn";

/*
 * Memoized on purpose: a picker holding hours, minutes and seconds renders up to
 * 144 options, and every keystroke in the input re-renders the popup. With stable
 * callbacks from TimePickerPopup only the column whose value changed re-renders.
 */
export const TimeColumn = memo(TimeColumnBase);

export default TimeColumn;
